import {
  FieldOperators,
  PostCallAnalysisItem,
  PostCallAnalysisItemType,
} from 'src/interfaces/agent.interface';

export const allowedOperatorsByAnalysisItemType: Record<
  PostCallAnalysisItemType,
  FieldOperators[]
> = {
  [PostCallAnalysisItemType.BOOLEAN]: [FieldOperators.EQ],
  [PostCallAnalysisItemType.NUMBER]: [
    FieldOperators.EQ,
    FieldOperators.NEQ,
    FieldOperators.GT,
    FieldOperators.GTE,
    FieldOperators.LT,
    FieldOperators.LTE,
  ],
  [PostCallAnalysisItemType.ENUM]: [FieldOperators.EQ, FieldOperators.NEQ],
  [PostCallAnalysisItemType.STRING]: [
    FieldOperators.EQ,
    FieldOperators.NEQ,
    FieldOperators.CONTAINS,
    FieldOperators.NOT_CONTAINS,
  ],
};

export type CoerceAndValidateRightResult =
  | { ok: true; right: boolean | number | string }
  | { ok: false; message: string };

export function isAllowedOperatorForAnalysisItem(
  item: PostCallAnalysisItem,
  operator: FieldOperators | undefined,
): boolean {
  if (!operator) return false;
  const allowed = allowedOperatorsByAnalysisItemType[item.type] ?? [];
  return allowed.includes(operator);
}

export function coerceAndValidateRight(params: {
  item: PostCallAnalysisItem;
  operator: FieldOperators | undefined;
  rightInput: string | boolean | null | undefined;
}): CoerceAndValidateRightResult {
  const { item, operator, rightInput } = params;

  if (!isAllowedOperatorForAnalysisItem(item, operator)) {
    const allowed = allowedOperatorsByAnalysisItemType[item.type] ?? [];
    return {
      ok: false,
      message: `Operator must be one of: ${allowed.join(', ')}`,
    };
  }

  if (item.type === PostCallAnalysisItemType.BOOLEAN) {
    if (typeof rightInput !== 'boolean') {
      return { ok: false, message: 'Value must be a boolean (True/False).' };
    }
    return { ok: true, right: rightInput };
  }

  if (item.type === PostCallAnalysisItemType.NUMBER) {
    if (typeof rightInput !== 'string') {
      return { ok: false, message: 'Value must be a number.' };
    }
    const trimmed = rightInput.trim();
    if (!trimmed) return { ok: false, message: 'Value must be a number.' };
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) {
      return {
        ok: false,
        message: 'Value must be a number (integer or decimal).',
      };
    }
    return { ok: true, right: parsed };
  }

  if (item.type === PostCallAnalysisItemType.ENUM) {
    if (typeof rightInput !== 'string') {
      return { ok: false, message: 'Value must be an enum option.' };
    }
    const enumOptions = item.enumOptions ?? [];
    if (!enumOptions.includes(rightInput)) {
      return { ok: false, message: 'Value must be one of the enum options.' };
    }
    return { ok: true, right: rightInput };
  }

  if (item.type === PostCallAnalysisItemType.STRING) {
    if (typeof rightInput !== 'string') {
      return { ok: false, message: 'Value must be a string.' };
    }
    return { ok: true, right: rightInput.trim() };
  }

  return { ok: false, message: 'Unsupported analysis item type.' };
}

export function sortAnalysisItemsForSelect(
  items: PostCallAnalysisItem[],
): PostCallAnalysisItem[] {
  return [...items].sort((a, b) =>
    (a.label || a.id)
      .toLowerCase()
      .localeCompare((b.label || b.id).toLowerCase()),
  );
}
